import type { ComponentChildren } from 'preact'
import { AlertTriangle, History, ShieldAlert } from 'lucide-preact'
import type {
  EmailBlastViewDto,
  EmailBlastGateDecisionDto,
  EmailBlastVariantResultDto,
  EmailBlastSplitTestSkipDto,
} from '@/types/emailBlast'
import { BlastStateBadge } from './BlastStateBadge'
import { RateMeter } from './RateMeter'
import { formatDateTime } from './engagementFormat'
import { lowerSafe } from '@/utils/strings'

// Presentational sections of the blast detail (ORB-348/350/354), shared by the deep-linkable detail
// route (ORB-384). Each takes the blast view and renders one self-contained section; the route composes
// them with the new Engagement + Recipients sections.

export function CircuitBreakerSection({ blast }: { blast: EmailBlastViewDto }) {
  const latestGate = blast.GateDecisions.length > 0 ? blast.GateDecisions[blast.GateDecisions.length - 1] : null
  return (
    <section className="space-y-3">
      <SectionHeading icon={<ShieldAlert className="w-4 h-4" />}>Circuit breaker</SectionHeading>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <RateMeter label="Hard bounce rate" value={blast.Stats.HardBouncePercent} max={blast.MaxHardBouncePercent} />
        <RateMeter label="Complaint rate" value={blast.Stats.ComplaintPercent} max={blast.MaxComplaintPercent} />
      </div>
      {blast.State === 'Paused' && latestGate?.Reason && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-300">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{latestGate.Reason}</span>
        </div>
      )}
    </section>
  )
}

export function SendStatsSection({ blast }: { blast: EmailBlastViewDto }) {
  return (
    <section className="space-y-3">
      <SectionHeading>Send stats</SectionHeading>
      {/* Audience funnel: imported → excluded → [registered after] → recipients → outcomes. Reads
          left-to-right so a drop between the audience and what shipped is self-explanatory. The
          "Registered after" stage only appears when someone registered post-staging and was pulled
          (ORB-409); without it the headline grid is an even 2×4 (Skipped/Complained demoted below). */}
      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat
          label="Audience"
          value={blast.TotalAudience + blast.ExcludedCount}
          hint="Distinct people in the imported audience, before exclusions."
        />
        <Stat
          label="Excluded"
          value={blast.ExcludedCount}
          hint="Audience members not sent to: already converted, suppressed (opt-out / hard bounce), invalid, or duplicate."
        />
        {blast.PulledAfterRegistrationCount > 0 && (
          <Stat
            label="Registered after"
            value={blast.PulledAfterRegistrationCount}
            hint="Registered during the reminder window and pulled from this send — so a registrant never gets a 'please register' nudge."
          />
        )}
        <Stat
          label="Recipients"
          value={blast.TotalAudience - blast.PulledAfterRegistrationCount}
          hint="Distinct recipients this blast will send to — audience minus exclusions and anyone who registered after staging."
        />
        <Stat
          label="Queued"
          value={blast.Stats.Queued}
          hint="Scheduled and not yet sent. Cancelled rows are counted under Skipped, not here."
        />
        <Stat label="Sent" value={blast.Stats.Sent} />
        <Stat label="Delivered" value={blast.Stats.Delivered} />
        <Stat label="Bounced" value={blast.Stats.Bounced} tone={blast.Stats.Bounced > 0 ? 'warn' : undefined} />
        <Stat label="Failed" value={blast.Stats.Failed} tone={blast.Stats.Failed > 0 ? 'danger' : undefined} />
      </dl>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        <span
          title="Recipients cancelled before send: blocked by the environment guard, suppressed (hard bounce / opt-out), or dropped by the blast circuit breaker. Not delivery failures."
          className="cursor-help underline decoration-dotted underline-offset-2"
        >
          Skipped
        </span>{' '}
        {blast.Stats.Skipped.toLocaleString()}
        {' · '}
        <span className={blast.Stats.Complained > 0 ? 'text-amber-600 dark:text-amber-400' : undefined}>
          Complained {blast.Stats.Complained.toLocaleString()}
        </span>
      </p>
    </section>
  )
}

export function RampProgressSection({ blast }: { blast: EmailBlastViewDto }) {
  return (
    <section className="space-y-3">
      <SectionHeading>Ramp progress</SectionHeading>
      <RampProgress stagePercents={blast.RampStagePercents} currentStage={blast.CurrentRampStage} />
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Canary size {blast.CanarySize.toLocaleString()} · {blast.EmailsPerMinute.toLocaleString()} emails/min ·
        {' '}measurement window {blast.MeasurementWindowMinutes} min
      </p>
    </section>
  )
}

export function StateHistorySection({ blast }: { blast: EmailBlastViewDto }) {
  return (
    <section className="space-y-3">
      <SectionHeading icon={<History className="w-4 h-4" />}>State history</SectionHeading>
      {blast.StateHistory.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">No transitions recorded yet.</p>
      ) : (
        <ol className="space-y-3">
          {blast.StateHistory.map((entry, i) => (
            <li key={`${entry.State}-${entry.Timestamp}-${i}`} className="flex items-start gap-3">
              <span className="mt-1 w-2 h-2 rounded-full bg-primary-500 shrink-0" aria-hidden="true" />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <BlastStateBadge state={entry.State} />
                  <span className="text-xs text-slate-400">{formatDateTime(entry.Timestamp)}</span>
                </div>
                {entry.Details && <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{entry.Details}</p>}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}

export function GateDecisionsSection({ blast }: { blast: EmailBlastViewDto }) {
  return (
    <section className="space-y-3">
      <SectionHeading>Gate decisions</SectionHeading>
      {blast.GateDecisions.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">No gate checks have run yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400">
              <tr>
                <th className="text-left font-medium px-3 py-2">When</th>
                <th className="text-left font-medium px-3 py-2">Decision</th>
                <th className="text-right font-medium px-3 py-2">Bounce %</th>
                <th className="text-right font-medium px-3 py-2">Complaint %</th>
                <th className="text-right font-medium px-3 py-2">Sample</th>
                <th className="text-left font-medium px-3 py-2">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {blast.GateDecisions.map((gate, i) => (
                <GateDecisionRow key={`${gate.Timestamp}-${i}`} gate={gate} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function GateDecisionRow({ gate }: { gate: EmailBlastGateDecisionDto }) {
  const paused = lowerSafe(gate.Decision).includes('pause')
  return (
    <tr className={paused ? 'bg-amber-50/60 dark:bg-amber-900/10' : undefined}>
      <td className="px-3 py-2 text-slate-500 dark:text-slate-400 whitespace-nowrap">{formatDateTime(gate.Timestamp)}</td>
      <td className="px-3 py-2">
        <span
          className={
            'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ' +
            (paused
              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
              : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200')
          }
        >
          {gate.Decision}
        </span>
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{gate.HardBouncePercent.toFixed(2)}%</td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{gate.ComplaintPercent.toFixed(2)}%</td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{gate.SampleSize.toLocaleString()}</td>
      <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{gate.Reason || '—'}</td>
    </tr>
  )
}

/**
 * A/B subject split test (ORB-453): per-variant results + the winner, when the blast is a split test.
 * When a test was configured but couldn't run (ORB-472) this renders the reason instead — the section
 * used to render nothing at all, so a skipped test was indistinguishable from never configuring one.
 */
export function SplitTestSection({ blast }: { blast: EmailBlastViewDto }) {
  if (!blast.IsSplitTest) {
    return blast.SplitTestSkip ? <SplitTestSkippedPanel skip={blast.SplitTestSkip} /> : null
  }
  const metricLabel = lowerSafe(blast.WinMetric ?? 'opens')
  return (
    <section className="space-y-3">
      <SectionHeading>Subject A/B test</SectionHeading>
      {blast.SplitTestResults.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Measuring {metricLabel} across {blast.SubjectVariantKeys.length} subjects; the winner is picked when the window closes.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400">
              <tr>
                <th className="text-left font-medium px-3 py-2">Variant</th>
                <th className="text-right font-medium px-3 py-2">Delivered</th>
                <th className="text-right font-medium px-3 py-2">Open rate</th>
                <th className="text-right font-medium px-3 py-2">Click rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {blast.SplitTestResults.map(result => (
                <SplitTestRow key={result.VariantKey} result={result} isWinner={result.VariantKey === blast.WinningVariantKey} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {blast.WinningVariantKey && (
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Subject <span className="font-semibold">{blast.WinningVariantKey}</span> won on {metricLabel}; it sends to the remaining recipients.
        </p>
      )}
    </section>
  )
}

/**
 * ORB-472: the touchpoint asked for a split test and this blast sent the control to everyone instead.
 * Degrading is correct — the real audience only exists once the send is staged — but it used to happen
 * silently, so a user who configured an A/B test just saw a normal send. This is the missing "why".
 */
function SplitTestSkippedPanel({ skip }: { skip: EmailBlastSplitTestSkipDto }) {
  return (
    <section className="space-y-3">
      <SectionHeading icon={<AlertTriangle className="w-4 h-4" />}>Subject A/B test</SectionHeading>
      <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-300">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="space-y-1">
          <p className="font-medium">
            Split test skipped — all {skip.EligibleCount.toLocaleString()} recipients get subject A, the control.
          </p>
          <p>{describeSplitTestSkip(skip)}</p>
        </div>
      </div>
    </section>
  )
}

/** Turns a skip record into the sentence that explains it, using the numbers the skip was decided on. */
function describeSplitTestSkip(skip: EmailBlastSplitTestSkipDto): string {
  const cohort = skip.CohortSize.toLocaleString()
  const eligible = skip.EligibleCount.toLocaleString()
  switch (skip.Reason) {
    case 'BelowPerVariantFloor':
      return (
        `Only ${cohort} of ${eligible} eligible recipients fell into the test cohort, but each of the ` +
        `${skip.VariantCount} subject lines needs at least ${skip.RequiredPerVariant.toLocaleString()} — ` +
        `${(skip.VariantCount * skip.RequiredPerVariant).toLocaleString()} between them. ` +
        'A bigger list or a larger cohort percentage would clear it.'
      )
    case 'NoChallengerSubject':
      return (
        'The email copy had only one subject line when this send was staged, so there was nothing to test ' +
        'the control against. Add a second subject in the copy builder before the next send.'
      )
    case 'NoRemainder':
      return (
        `The test cohort covered the whole audience (${cohort} of ${eligible} eligible recipients), leaving ` +
        'nobody for the winning subject to be sent to. Lower the cohort percentage.'
      )
    case 'RecipientLocal':
      return (
        "This send goes out at each recipient's own local time, so there is no shared window in which to " +
        'compare the subject lines. Send at a fixed time to run a test.'
      )
    default:
      // Unreachable via the API (it rejects an unrecognized reason on read) — but a render throw blanks
      // the whole page, so name the raw value instead of hiding it.
      return `The test could not run (${String(skip.Reason)}).`
  }
}

function SplitTestRow({ result, isWinner }: { result: EmailBlastVariantResultDto; isWinner: boolean }) {
  const rate = (n: number) => (result.Delivered > 0 ? `${((n / result.Delivered) * 100).toFixed(1)}%` : '—')
  return (
    <tr className={isWinner ? 'bg-green-50/60 dark:bg-green-900/10' : undefined}>
      <td className="px-3 py-2">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded text-[11px] font-bold bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200">{result.VariantKey}</span>
          {isWinner && <span className="text-[10px] font-bold uppercase tracking-wide text-green-700 dark:text-green-400">Winner</span>}
        </span>
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{result.Delivered.toLocaleString()}</td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{rate(result.Opened)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{rate(result.Clicked)}</td>
    </tr>
  )
}

/** Stepped ramp indicator: completed/active/upcoming stages of RampStagePercents. */
function RampProgress({ stagePercents, currentStage }: { stagePercents: number[]; currentStage: number }) {
  if (stagePercents.length === 0) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">No ramp stages configured.</p>
  }
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {stagePercents.map((pct, i) => {
        const done = i < currentStage
        const active = i === currentStage
        const tone = done
          ? 'bg-primary-600 text-white border-primary-600'
          : active
          ? 'bg-primary-100 text-primary-800 border-primary-400 dark:bg-primary-900/30 dark:text-primary-300 dark:border-primary-600'
          : 'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'
        return (
          <span
            key={`${pct}-${i}`}
            className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${tone}`}
            aria-current={active ? 'step' : undefined}
          >
            {pct}%
          </span>
        )
      })}
      <span className="text-xs text-slate-500 dark:text-slate-400">
        Stage {Math.min(currentStage + 1, stagePercents.length)} of {stagePercents.length}
      </span>
    </div>
  )
}

export function SectionHeading({ icon, children }: { icon?: ComponentChildren; children: ComponentChildren }) {
  return (
    <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
      {icon && <span className="text-slate-400 dark:text-slate-500">{icon}</span>}
      {children}
    </h3>
  )
}

function Stat({ label, value, tone, hint }: { label: string; value: number; tone?: 'warn' | 'danger'; hint?: string }) {
  const valueColor =
    tone === 'danger'
      ? 'text-red-600 dark:text-red-400'
      : tone === 'warn'
      ? 'text-amber-600 dark:text-amber-400'
      : 'text-slate-900 dark:text-white'
  return (
    <div>
      <dt
        title={hint}
        className={
          'text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500' +
          (hint ? ' cursor-help underline decoration-dotted underline-offset-2' : '')
        }
      >
        {label}
      </dt>
      <dd className={`mt-1 text-lg font-semibold tabular-nums ${valueColor}`}>{value.toLocaleString()}</dd>
    </div>
  )
}
